import { getCustomProps } from '../forms'
import { getData } from '../get-table'
import { HttpService } from '../http.service'

const http = new HttpService()

export interface Bases {
  parentConceptId?: string
  childConceptId: string
  amountModifier: string
  quantityModifier: string
  percentageModifier: number
  isHistoric: boolean
  isProjectable: boolean
  isGroup: boolean
  customFields: object
  action: string
}

export interface PayrollConcepts {
  payrollId: string
  masterConceptId: string
  creditAccountId: string
  debitAccountId: string
  processes: string[]
  conceptFormatId: string
  journeys: string[]
  savedValidationId: string
  nameAlternate: string
  codeAlternate: string
  nameOperation: string
  descriptionOperation: string
  percentage: number
  amountFormula: string
  quantityFormula: string
  validation: string
  amountSavedValidationId: string
  quantitySavedValidationId: string
  amountValidation: any
  quantityValidation: any
  bases: Bases[]
}

export async function getPayrollConcepts(
  offset: number,
  perPage: number,
  dataOrder: string,
  search: string = '',
  countryId?: string
): Promise<any> {
  // where's
  const where = ['concepts.payroll_id=:payrollId']
  if (search) {
    where.push(
      `CONCAT(concepts.name,concepts.code,concept_formats.name,master_concepts.code,master_concepts.name) ILIKE '%${search}%'`
    )
  }
  // order by
  let modifier = []
  dataOrder !== ''
    ? dataOrder[1] === 'code'
      ? (modifier = [
          `ORDER BY (CASE WHEN concepts.code ~ '^[0-9]+$' THEN CAST(concepts.code AS NUMERIC) ELSE NULL END) ${dataOrder[0]}, concepts.code ${dataOrder[0]}`
        ])
      : (modifier = [`order by ${dataOrder[1]} ${dataOrder[0]}`])
    : (modifier = [
        "ORDER BY (CASE WHEN concepts.code ~ '^[0-9]+$' THEN CAST(concepts.code AS NUMERIC) ELSE NULL END), concepts.code"
      ])
  // result
  const queryUy = [
    `concepts.custom_fields ->> 'custom:uyCodeAgrupationBPS' AS uycodeagrupationbps`,
    `concepts.custom_fields ->> 'custom:uyCodeGroupPrefactureBPS' AS uycodegroupprefacturebps`
  ]
  const queryMx = [
    `fiscal_rules.name AS fiscalrule`,
    `taxed_concept.name AS taxedconcept`,
    `exempt_concept.name AS exemptconcept`
  ]
  const joinMx = [
    `LEFT JOIN fiscal_rules ON fiscal_rules.id::varchar = (concepts.custom_fields ->> 'custom:fiscalRule')::varchar`,
    `LEFT JOIN concepts AS taxed_concept ON taxed_concept.id::varchar = (concepts.custom_fields ->> 'custom:taxedConcept')::varchar`,
    `LEFT JOIN concepts AS exempt_concept ON exempt_concept.id::varchar = (concepts.custom_fields ->> 'custom:exemptConcept')::varchar`
  ]
  const response: any = await getData({
    tables: ['pg_concepts'],
    fields: [
      'concepts.id',
      'concepts.code',
      'concepts.name',
      "CONCAT(master_concepts.code,' - ',master_concepts.name) as masterConcept",
      "CONCAT_WS('-',lac.code,lac.name) as creditAccountId",
      "CONCAT_WS('-',lad.code,lad.name) as debitAccountId",
      'concept_formats.name as format',
      ...(countryId === 'UY' ? queryUy : []),
      ...(countryId === 'MX' ? queryMx : []),
      'concepts.is_inactive',
      'concepts.updated_at'
    ],
    join: [
      'INNER JOIN master_concepts ON concepts.master_concept_id=master_concepts.id',
      'INNER JOIN concept_formats ON concept_formats.id=concepts.concept_format_id',
      'LEFT JOIN ledger_accounts lac ON lac.id=concepts.credit_account_id',
      'LEFT JOIN ledger_accounts lad ON lad.id=concepts.debit_account_id',
      ...(countryId === 'MX' ? joinMx : [])
    ],
    where,
    modifier,
    options: {
      outputFormat: 'json',
      pagination: { offset, limit: perPage }
    }
  })
  return response
}

export async function getPayrollConcept(id: string): Promise<PayrollConcepts> {
  const data = await getData<any>({
    tables: ['pg_concepts'],
    fields: [
      'concepts.id',
      'concepts.payroll_id as payrollId',
      'concepts.master_concept_id as masterConceptId',
      'concepts.credit_account_id as creditAccountId',
      'concepts.debit_account_id as debitAccountId',
      'concepts.processes',
      'concepts.concept_format_id as conceptFormatId',
      'concepts.journeys',
      'concepts.code',
      'concepts.name',
      'concept_types.code as conceptTypeId',
      'concepts.code_alternate as codeAlternate',
      'concepts.name_alternate as nameAlternate',
      'concepts.name_operation as nameOperation',
      'concepts.description_operation as descriptionOperation',
      'concepts.percentage',
      'concepts.amount_formula as amountFormula',
      'concepts.quantity_formula as quantityFormula',
      'concepts.quantity_saved_validation_id as quantitySavedValidationId',
      'concepts.amount_saved_validation_id as amountSavedValidationId',
      'concepts.amount_validation as amountValidation',
      'concepts.quantity_validation as quantityValidation',
      'concepts.custom_fields as custom'
    ],
    where: [`concepts.id = '${id}'`],
    join: [
      'INNER JOIN master_concepts ON master_concepts.id = concepts.master_concept_id',
      'INNER JOIN concept_types ON concept_types.id = master_concepts.concept_type_id'
    ],
    options: {
      outputFormat: 'json',
      pagination: { offset: 0, limit: 10 }
    }
  })
  return data[0].results[0]
}

export async function createPayrollConcept(
  data: PayrollConcepts
): Promise<boolean> {
  for (const base of data.bases) {
    base.customFields = {
      ...getCustomProps(base)
    }
  }
  const response: any = await http.post('concept', {
    payrollId: data.payrollId,
    masterConceptId: data.masterConceptId,
    creditAccountId: data.creditAccountId,
    debitAccountId: data.debitAccountId,
    processes: data.processes,
    conceptFormatId: data.conceptFormatId,
    journeys: data.journeys,
    savedValidationId: data.savedValidationId,
    nameAlternate: data.nameAlternate,
    codeAlternate: data.codeAlternate,
    nameOperation: data.nameOperation,
    descriptionOperation: data.descriptionOperation,
    percentage: data.percentage,
    amountFormula: data.amountFormula,
    quantityFormula: data.quantityFormula,
    validation: data.validation,
    amountSavedValidationId: data.amountSavedValidationId,
    quantitySavedValidationId: data.quantitySavedValidationId,
    amountValidation: data.amountValidation === '' ? [] : data.amountValidation,
    quantityValidation:
      data.quantityValidation === '' ? [] : data.quantityValidation,
    bases: data.bases,
    customFields: {
      ...getCustomProps(data)
    }
  })

  if (response?.data?.data?.error) {
    return false
  }

  return true
}

export async function updatePayrollConcept(
  data: PayrollConcepts,
  id: string
): Promise<boolean> {
  for (const base of data.bases) {
    delete base.parentConceptId
    base.customFields = {
      ...getCustomProps(base)
    }
  }
  const response: any = await http.put('concept/' + id, {
    payrollId: data.payrollId,
    masterConceptId: data.masterConceptId,
    creditAccountId: data.creditAccountId,
    debitAccountId: data.debitAccountId,
    processes: data.processes,
    conceptFormatId: data.conceptFormatId,
    journeys: data.journeys,
    savedValidationId: data.savedValidationId,
    nameAlternate: data.nameAlternate,
    codeAlternate: data.codeAlternate,
    nameOperation: data.nameOperation,
    descriptionOperation: data.descriptionOperation,
    percentage: data.percentage,
    amountFormula: data.amountFormula,
    quantityFormula: data.quantityFormula,
    validation: data.validation,
    amountSavedValidationId: data.amountSavedValidationId,
    quantitySavedValidationId: data.quantitySavedValidationId,
    amountValidation: data.amountValidation === '' ? [] : data.amountValidation,
    quantityValidation:
      data.quantityValidation === '' ? [] : data.quantityValidation,
    bases: data.bases,
    customFields: {
      ...getCustomProps(data)
    }
  })

  if (response?.data?.data?.error) {
    return false
  }

  return true
}

export async function updateStatePayrollConcept(
  id: string,
  enable: boolean
): Promise<boolean> {
  const response: any = await http.put(`concept/${id}/enable/`, {
    enable
  })

  if (response?.data?.data?.error) {
    return false
  }

  return true
}

export async function deletePayrollConcept(id: string): Promise<boolean> {
  const response: any = await http.delete('concept/' + id)

  if (response?.data?.data?.error) {
    return false
  }

  return true
}

export async function getFiscalRule(id: string): Promise<any> {
  const response: any = await getData({
    tables: ['pg_fiscal_rules'],
    fields: [
      `CASE
      WHEN custom_fields ->> 'custom:mxApplySegregation' = 'true' THEN true
      WHEN custom_fields ->> 'custom:mxApplySegregation' = 'false' THEN false
      ELSE false
      END as check`
    ],

    where: [`id='${id}'`],

    options: {
      outputFormat: 'json'
    }
  })

  return response[0].results[0].check
}